import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Shield, Smartphone, Mail, Loader2, ShieldCheck, KeyRound, Copy, LogOut } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://kroytechnologygroup.com/wp-content/uploads/2022/01/KTG-Final-Logo-Flat-300x200.png";

export default function MFASetupPage() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [step, setStep] = useState('choose'); // choose | setup | done
  const [method, setMethod] = useState(null);
  const [setupData, setSetupData] = useState(null);
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [backupCodes, setBackupCodes] = useState(null);

  useEffect(() => {
    if (!user) navigate('/login', { replace: true });
    else if (!user.is_admin || user.mfa_enabled) navigate(user.is_admin ? '/admin' : '/dashboard', { replace: true });
  }, [user, navigate]);

  const startSetup = async (m) => {
    setMethod(m);
    setStep('setup');
    try {
      const r = await axios.post(`${API_URL}/auth/mfa/setup`, { method: m }, { headers: { Authorization: `Bearer ${token}` } });
      setSetupData(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start setup');
      setStep('choose');
    }
  };

  const verifySetup = async (e) => {
    e.preventDefault();
    if (!code.trim()) { toast.error('Enter a verification code'); return; }
    setVerifying(true);
    try {
      const r = await axios.post(`${API_URL}/auth/mfa/verify-setup`, { code: code.trim() }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(r.data.message);
      setBackupCodes(r.data.backup_codes);
      setStep('done');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally { setVerifying(false); }
  };

  const finish = () => {
    // Reload user data to get updated mfa_enabled flag
    window.location.href = '/admin';
  };

  const copyBackupCodes = () => {
    if (backupCodes) {
      navigator.clipboard.writeText(backupCodes.join('\n'));
      toast.success('Backup codes copied');
    }
  };

  if (!user || !user.is_admin) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
      <div className="w-full max-w-lg">
        {/* Header */}
        <div className="text-center mb-8">
          <img src={LOGO_URL} alt="AuditGuard" className="h-14 w-auto rounded-lg mx-auto mb-4 bg-white p-1.5 shadow-sm" />
          <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-2 mb-4">
            <Shield className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">MFA Required for Admin Accounts</span>
          </div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Set Up Two-Factor Authentication
          </h1>
          <p className="text-slate-500 mt-2 text-sm">
            As an admin, you must enable MFA before accessing the portal.
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8" data-testid="mfa-setup-page">
          {step === 'choose' && (
            <div data-testid="mfa-setup-choose">
              <p className="text-sm text-slate-600 mb-6">Choose your preferred verification method:</p>
              <div className="space-y-4">
                <button
                  type="button"
                  onClick={() => startSetup('totp')}
                  className="w-full p-5 border-2 border-slate-200 rounded-xl hover:border-[#FF6B35] hover:bg-[#FF6B35]/5 transition-all text-left flex items-start gap-4 group"
                  data-testid="mfa-setup-choose-totp"
                >
                  <div className="w-12 h-12 bg-slate-100 group-hover:bg-[#FF6B35]/10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                    <Smartphone className="h-6 w-6 text-slate-400 group-hover:text-[#FF6B35] transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">Authenticator App</h3>
                    <p className="text-xs text-slate-500 mt-1">Use Google Authenticator, Authy, or Microsoft Authenticator. Most secure option.</p>
                  </div>
                </button>
                <button
                  type="button"
                  onClick={() => startSetup('email')}
                  className="w-full p-5 border-2 border-slate-200 rounded-xl hover:border-[#FF6B35] hover:bg-[#FF6B35]/5 transition-all text-left flex items-start gap-4 group"
                  data-testid="mfa-setup-choose-email"
                >
                  <div className="w-12 h-12 bg-slate-100 group-hover:bg-[#FF6B35]/10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors">
                    <Mail className="h-6 w-6 text-slate-400 group-hover:text-[#FF6B35] transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">Email Verification</h3>
                    <p className="text-xs text-slate-500 mt-1">Receive a 6-digit code via email each time you sign in.</p>
                  </div>
                </button>
              </div>
              <div className="mt-6 pt-4 border-t border-slate-100 text-center">
                <button onClick={logout} className="text-sm text-slate-400 hover:text-slate-600 inline-flex items-center gap-1.5" data-testid="mfa-setup-logout">
                  <LogOut className="h-3.5 w-3.5" /> Sign out
                </button>
              </div>
            </div>
          )}

          {step === 'setup' && (
            <div data-testid="mfa-setup-verify">
              {method === 'totp' && setupData?.qr_code && (
                <>
                  <p className="text-sm font-medium text-slate-700 mb-4">Scan this QR code with your authenticator app:</p>
                  <div className="flex flex-col items-center gap-3 p-4 bg-slate-50 rounded-xl mb-5">
                    <img src={`data:image/png;base64,${setupData.qr_code}`} alt="QR Code" className="w-44 h-44" data-testid="mfa-setup-qr" />
                    <div className="text-center">
                      <p className="text-xs text-slate-400">Or enter manually:</p>
                      <code className="text-xs bg-white px-3 py-1 rounded font-mono select-all border border-slate-200" data-testid="mfa-setup-secret">{setupData.secret}</code>
                    </div>
                  </div>
                  <p className="text-sm font-medium text-slate-700 mb-2">Enter the 6-digit code from your app:</p>
                </>
              )}
              {method === 'email' && (
                <p className="text-sm text-slate-600 mb-4">A verification code has been sent to <strong>{user.email}</strong>. Enter it below:</p>
              )}
              <form onSubmit={verifySetup} className="space-y-4">
                <Input
                  type="text"
                  inputMode="numeric"
                  maxLength={8}
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\s/g, ''))}
                  placeholder="Enter code"
                  className="h-14 text-center text-2xl tracking-[0.3em] font-mono"
                  autoFocus
                  data-testid="mfa-setup-code-input"
                  required
                />
                <Button type="submit" className="w-full h-12 btn-primary text-base gap-2" disabled={verifying} data-testid="mfa-setup-verify-btn">
                  {verifying ? <><Loader2 className="h-5 w-5 animate-spin" /> Verifying...</> : <><ShieldCheck className="h-5 w-5" /> Verify &amp; Enable MFA</>}
                </Button>
              </form>
              <button
                type="button"
                onClick={() => { setStep('choose'); setSetupData(null); setCode(''); }}
                className="mt-4 text-sm text-slate-400 hover:text-slate-600 mx-auto block"
                data-testid="mfa-setup-back"
              >
                Choose a different method
              </button>
            </div>
          )}

          {step === 'done' && (
            <div className="text-center" data-testid="mfa-setup-complete">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <ShieldCheck className="h-8 w-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>MFA Enabled!</h2>
              <p className="text-sm text-slate-500 mb-6">Your account is now protected with two-factor authentication.</p>

              {backupCodes && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 text-left mb-6">
                  <h3 className="text-sm font-semibold text-amber-800 mb-2 flex items-center gap-2">
                    <KeyRound className="h-4 w-4" /> Backup Recovery Codes
                  </h3>
                  <p className="text-xs text-amber-700 mb-3">Save these codes securely. Each can be used once if you lose access to your MFA method.</p>
                  <div className="bg-white rounded-lg p-3 border border-amber-200 font-mono text-sm grid grid-cols-2 gap-1.5">
                    {backupCodes.map((c, i) => <span key={i} className="text-slate-800">{c}</span>)}
                  </div>
                  <Button type="button" variant="outline" size="sm" className="mt-3 gap-1.5" onClick={copyBackupCodes} data-testid="mfa-setup-copy-codes">
                    <Copy className="h-3.5 w-3.5" /> Copy All
                  </Button>
                </div>
              )}

              <Button onClick={finish} className="w-full h-12 btn-primary text-base gap-2" data-testid="mfa-setup-continue">
                Continue to Admin Portal
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
